import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Select as NhsUkSelect } from 'nhsuk-react-components';

const Select = forwardRef((props, ref) => {
    const [error, setError] = useState(undefined);
    const [isReadOnly, setIsReadOnly] = useState(undefined);
    const [isValid, setIsValid] = useState(undefined);

    useEffect(() => {
        setIsReadOnly(props.question.isReadOnly);
    }, [props.question.isReadOnly]);

    useEffect(() => {
        if (
            props.onConditionalReveal &&
            props.question.value !== undefined &&
            props.question.value !== null
        ) {
            toggleConditionalRevealQuestions(props.question.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.question.value]);

    useImperativeHandle(ref, () => ({
        validate,
        isValid
    }));

    const validate = () => {
        if (props.question.mandatory) {
            if (!props.question.value) {
                setError(`Enter the ${props.question.name}`);
                setIsValid(false);
            } else {
                setError(undefined);
                setIsValid(true);
            }
        } else {
            setError(undefined);
            setIsValid(true);
        }
    };

    const normaliseQuestionRefs = questionRefs => {
        if (!questionRefs) {
            return [];
        }

        return Array.isArray(questionRefs) ? questionRefs : [questionRefs];
    };

    const getQuestionIds = questionRefs => {
        return normaliseQuestionRefs(questionRefs)
            .map(question => (typeof question === 'string' ? question : question?.id))
            .filter(Boolean);
    };

    const getOptionChildIds = option => {
        if (option?.hide_linked_question === false && !option?.linked_question_id) {
            return getQuestionIds(props.question.linked_questions);
        }

        return getQuestionIds([
            ...(option?.questions || []),
            ...(option?.linked_question_id
                ? Array.isArray(option.linked_question_id)
                    ? option.linked_question_id
                    : [option.linked_question_id]
                : [])
        ]);
    };

    const toggleConditionalRevealQuestions = value => {
        const allChildIds = props.question.options.flatMap(getOptionChildIds);

        if (!value) {
            props.onConditionalReveal({
                isRevealed: false,
                questionIds: allChildIds
            });
            return;
        }

        const selectedOption = props.question.options.find(option => option.key === value);
        const revealIds = new Set(getOptionChildIds(selectedOption));

        props.onConditionalReveal({
            isRevealed: false,
            questionIds: allChildIds.filter(id => !revealIds.has(id))
        });

        props.onConditionalReveal({
            isRevealed: true,
            questionIds: Array.from(revealIds)
        });
    };

    const handleChange = async event => {
        await props.onChange({
            questionId: props.question.id,
            linked_questions: props.question.linked_questions || null,
            option: props.question.options.find(x => x.key === event.target.value) || null,
            value: event.target.value
        });

        if (props.onConditionalReveal && event.type === 'change') {
            toggleConditionalRevealQuestions(event.target.value);
        }
    };

    const isInOptions = props.question.options.some(option => option.key === props.question.value);
    const value = isInOptions ? props.question.value : '';

    return (
        <NhsUkSelect
            label={props.question.name}
            hint={props.question.hint_text}
            value={value}
            onChange={handleChange}
            error={error}
            disabled={isReadOnly}
            required={props.question.mandatory}
        >
            <NhsUkSelect.Option value="">Please Select</NhsUkSelect.Option>
            {props.question.options.map(option => (
                <NhsUkSelect.Option key={option.key} value={option.key}>
                    {option.value}
                </NhsUkSelect.Option>
            ))}
        </NhsUkSelect>
    );
});

export default Select;
